import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';

import { filter, Observable, tap } from 'rxjs';
import { isDefined } from '@evorait/pace-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import {
    disableMeasurementEquipmentTable,
    updateMeasurementEquipmentRowVisibility,
} from '../../store/actions/measurement-equipments.actions';
import { getFormGlobalReadonly, getMeasuringEquipments } from '../../store/selectors/form.selector';
import {
    MeasuringEquipments,
    MeasuringEquipmentsControlName,
    MeasuringEquipmentTableRow,
} from '../../types/measuring-equipments';
import { faCheck, faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { CHECKPOINTS_STATUS } from '../../types/checkpoints-status';
import moment from 'moment';
import { FormsConstants } from '../../types/forms-constants';
import { MeasurementEquipmentUtilities } from '../../utilities/measurement-equipment.utilities';

@Component({
    selector: 'pace-forms-measurement-equipments',
    templateUrl: './measurement-equipments.component.html',
    styleUrl: './measurement-equipments.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasurementEquipmentsComponent {
    public measuringEquipments$: Observable<MeasuringEquipments>;
    public readOnly$: Observable<boolean>;
    private store = inject(Store);
    private cdr = inject(ChangeDetectorRef);
    private destroy = inject(DestroyRef);

    public measurementEquipmentsOpen = false;
    public totalRows: number;
    public rowsWithData: number;
    private initialLoadComplete = false;
    protected readonly faCheck = faCheck;
    public readonly faTrashAlt = faTrashAlt;
    public readonly faPlus = faPlus;
    public constructor() {
        this.measuringEquipments$ = this.store.pipe(
            select(getMeasuringEquipments),
            takeUntilDestroyed(this.destroy),
            filter(isDefined),
            tap((equipments) => {
                this.totalRows = equipments.rows?.filter((row) => row.visibility).length;
                this.rowsWithData = equipments.rows?.filter(
                    (row) => row.visibility && row.columns.every((column) => column.value !== ''),
                ).length;
                if (equipments.noMeasurementToolsUsed) {
                    this.rowsWithData = this.totalRows;
                    if (
                        equipments.rows[0].columns.some(
                            (column) => column.value === FormsConstants.NA && !this.initialLoadComplete,
                        )
                    ) {
                        this.store.dispatch(
                            disableMeasurementEquipmentTable({ rowId: equipments.rows[0].rowId, noToolsUsed: true }),
                        );
                    }
                }
                this.initialLoadComplete = true;
                this.cdr.detectChanges();
            }),
        );

        this.readOnly$ = this.store.pipe(select(getFormGlobalReadonly), takeUntilDestroyed(this.destroy));
    }

    public addRow(rows: MeasuringEquipmentTableRow[]): void {
        const rowId = rows.find((row) => !row.visibility)?.rowId;
        if (rowId) {
            this.store.dispatch(updateMeasurementEquipmentRowVisibility({ rowId: rowId, visibility: true }));
        }
    }

    public deleteRow(rowId: string): void {
        this.store.dispatch(updateMeasurementEquipmentRowVisibility({ rowId: rowId, visibility: false }));
    }

    public noMeasurementToolsUsed(rowId: string, event: Event): void {
        const checkbox = event.target as HTMLInputElement;
        this.store.dispatch(disableMeasurementEquipmentTable({ rowId, noToolsUsed: checkbox.checked }));
    }

    public measurementEquipmentsOpenChanged(open: boolean): void {
        this.measurementEquipmentsOpen = open;
        this.cdr.detectChanges();
    }

    public updateMeasurementEquipmentsStatus(measuringEquipments: MeasuringEquipments): CHECKPOINTS_STATUS {
        const isFilled = measuringEquipments.rows
            .filter((row) => row.visibility)
            .every((row) => row.columns.every((column) => column.value !== ''));

        const dateOutOfBoundaryRow = measuringEquipments.rows
            .filter((row) => row.visibility)
            .some((row) => {
                const calibratedDate = row.columns.find(
                    (col) => col.columnName === MeasuringEquipmentsControlName.Calibrated,
                );

                const dateUsed = MeasurementEquipmentUtilities.getDateUsed(row);

                return isDefined(calibratedDate) && moment(calibratedDate.value).isBefore(moment(dateUsed));
            });

        const isEmpty = measuringEquipments.rows
            .filter((row) => row.visibility)
            .every((row) => row.columns.every((column) => column.value === ''));

        if (measuringEquipments.noMeasurementToolsUsed) {
            return CHECKPOINTS_STATUS.COMPLETELY_FILLED;
        }

        if (!isFilled && !isEmpty) {
            return dateOutOfBoundaryRow
                ? CHECKPOINTS_STATUS.PARTIALLY_FILLED_NOT_OK
                : CHECKPOINTS_STATUS.PARTIALLY_FILLED;
        }

        if (isFilled && !isEmpty) {
            return dateOutOfBoundaryRow
                ? CHECKPOINTS_STATUS.COMPLETELY_FILLED_NOT_OK
                : CHECKPOINTS_STATUS.COMPLETELY_FILLED;
        } else {
            return CHECKPOINTS_STATUS.EMPTY;
        }
    }

    public trackByRowId(index: number, row: MeasuringEquipmentTableRow): string {
        return row.rowId;
    }
}
